import React, { Fragment, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { Panel } from '@printdeal/ui-library';
import { AlternativeUpload, IAlternativeUpload } from '@printdeal/alternative-upload';
import ComplaintDetailAttachment from './ComplaintDetailAttachment';
import { ServiceFactory } from '../../../../../services/ServiceFactory';
import { Complaint, Attachment } from '../../../../../types/complaints';

const STATES = {
  IDLE: 'idle',
  ADDING: 'adding',
};

interface Props {
  complaint: Complaint;
  attachments: Attachment[];
}

const getAttachmentDetails = (attachments: IAlternativeUpload[]) =>
  attachments.map((upload) => {
    const {
      urls, fileType, uploadTitle, id,
    } = upload;

    return {
      id,
      href: urls?.originalUrl,
      contentType: fileType,
      description: uploadTitle,
    };
  });

const ComplaintDetailAttachments = ({ complaint, attachments }: Props) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState(STATES.IDLE);
  const isResolved = complaint.status === 'resolved';

  const handleAttachment = async (uploads: IAlternativeUpload[]) => {
    if (uploads) {
      setStatus(STATES.ADDING);
      const formattedAttachments = getAttachmentDetails(uploads);
      const complaintService = ServiceFactory.createComplaintsService();
      await complaintService.addAttachments(complaint.complaintId, formattedAttachments as Attachment[]);
      await queryClient.refetchQueries({
        queryKey: ['complaint', complaint.complaintId],
      });
      setStatus(STATES.IDLE);
    }
  };

  return (
    <>
      {(!isResolved || attachments.length > 0) && (
        <Panel className="pd-mt-6">
          <Panel.Header title={<FormattedMessage id="complaints.details.attachments.title" defaultMessage="Attachments" />} />
          {attachments.map((attachment, index) => (
            <Fragment key={attachment.id}>
              <ComplaintDetailAttachment
                complaint={complaint}
                attachment={attachment}
              />
              {index + 1 !== attachments.length && <hr className="pd-my-5" />}
            </Fragment>
          ))}
          {complaint.status !== 'resolved' && (
            <form className={attachments.length > 0 ? 'pd-mt-8' : ''}>
              <fieldset disabled={status === STATES.ADDING} className="pd-h-full pd-rounded pd-border pd-p-5">
                <AlternativeUpload
                  id="complaint-detail"
                  name="complaint-detail"
                  key={attachments.length}
                  onChange={handleAttachment}
                  dataLayerData={{ location: 'complaint_detail_attachment' }}
                />
              </fieldset>
            </form>
          )}
        </Panel>
      )}
    </>
  );
};

export default ComplaintDetailAttachments;
